import * as React from "react";

import { validateUploadFiles } from "@/components/_organisms/tiptap/tiptap-node/pdf-upload-node/validate-upload-files";

interface FileItem {
  id: string;
  file: File;
  progress: number;
  status: "uploading" | "success" | "error";
  url?: string;
  abortController?: AbortController;
  error?: string;
}

interface UploadOptions {
  accept: string;
  limit: number;
  maxSize: number;
  upload: (file: File, onProgress: (event: { progress: number }) => void, signal: AbortSignal) => Promise<string>;
  onError?: (error: Error) => void;
  onSuccess?: (url: string) => void;
}

export function useFileUpload(options: UploadOptions) {
  const [fileItem, setFileItem] = React.useState<FileItem | null>(null);

  const uploadFile = async (file: File): Promise<string | null> => {
    const abortController = new AbortController();

    setFileItem({
      id: crypto.randomUUID(),
      file,
      progress: 0,
      status: "uploading",
      abortController,
    });

    try {
      if (!options.upload) {
        throw new Error("Upload function is not defined");
      }

      const url = await options.upload(
        file,
        (event: { progress: number }) => {
          setFileItem((previousFileItem) => {
            if (!previousFileItem) {
              return null;
            }
            return { ...previousFileItem, progress: event.progress };
          });
        },
        abortController.signal,
      );

      if (!url) {
        throw new Error(`Upload of "${file.name}" failed: the server did not return a URL. Please try again.`);
      }

      if (abortController.signal.aborted) {
        return null;
      }

      setFileItem((previousFileItem) => {
        if (!previousFileItem) {
          return null;
        }
        return { ...previousFileItem, status: "success", url, progress: 100 };
      });
      options.onSuccess?.(url);
      return url;
    } catch (error) {
      if (!abortController.signal.aborted) {
        setFileItem((previousFileItem) => {
          if (!previousFileItem) {
            return null;
          }
          return { ...previousFileItem, status: "error", progress: 0 };
        });
        options.onError?.(error instanceof Error ? error : new Error(`Upload of "${file.name}" failed. Please try again.`));
      }
      return null;
    }
  };

  const uploadFiles = async (files: File[]): Promise<string | null> => {
    const validation = validateUploadFiles({ files, limit: options.limit, maxSize: options.maxSize });

    if (validation.error) {
      options.onError?.(validation.error);
      return null;
    }

    return uploadFile(validation.file);
  };

  const clearFileItem = () => {
    if (!fileItem) {
      return;
    }

    fileItem.abortController?.abort();
    if (fileItem.url) {
      URL.revokeObjectURL(fileItem.url);
    }
    setFileItem(null);
  };

  return {
    fileItem,
    uploadFiles,
    clearFileItem,
  };
}
